import { Row, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { QuestionsRightsTypes } from "..";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
} from "shared/index";
import { OpenQuestion } from "anketa-core/open_question";
import { NumericValidator, TextValidator } from "anketa-core/validation";
import { NumericQuestion } from "anketa-core/numeric_question";
import { getTranslation } from "src/internationalization";
import { ComplexFactory } from "anketa-core/base";
import { User } from "anketa-core/complex";
import { ComplexQuestion } from "anketa-core/complex_question";
import { Vpc } from "../../../../";
import { OpsanioWaves } from "../../../";
import { generateSwitch } from "../functions";

const generateSysVol = (
  changeListener: () => void,
  oldValues: any,
  name: string,
  initSize: number,
  mount: string,
  fstype: string,
  options: string,
  min: number = -1,
): Row => {
  const rootKey = name.toLowerCase().replaceAll(" ", "_");

  const row: Row = new Row(`sys_vol_${rootKey}`, [
    new Translation("en", ""),
    new Translation("de", ""),
  ]);

  const nameQ = new OpenQuestion(`sys_vol_${rootKey}_name`, [
    new Translation("en", "Name"),
    new Translation("de", "Name"),
  ]);
  const nameValue =
    oldValues?.[`hardware.sys_vol.sys_vol_${rootKey}.sys_vol_${rootKey}_name`]
      ?.value;

  nameQ.validator = new TextValidator();
  if (nameValue !== undefined && nameValue !== null) {
    nameQ.setFactValue(nameValue);
  } else {
    nameQ.setFactValue(name);
  }
  row.addChild(nameQ);

  const size = new NumericQuestion(`sys_vol_${rootKey}_size`, [
    new Translation("en", "Size (in GiB)"),
    new Translation("de", "Size (in GiB)"),
  ]);
  size.addChangeListener(() => {
    changeListener();
  });
  const validator = new NumericValidator();
  validator.min = min;

  size.validator = validator;
  const sizeValue =
    oldValues?.[`hardware.sys_vol.sys_vol_${rootKey}.sys_vol_${rootKey}_size`]
      ?.value;

  if (sizeValue !== undefined && sizeValue !== null) {
    size.setFactValue(sizeValue);
  } else {
    size.setFactValue(initSize);
  }
  row.addChild(size);

  const mountQ = new OpenQuestion(`sys_vol_${rootKey}_mount`, [
    new Translation("en", "Mount"),
    new Translation("de", "Mount"),
  ]);
  const mountValue =
    oldValues?.[`hardware.sys_vol.sys_vol_${rootKey}.sys_vol_${rootKey}_mount`]
      ?.value;
  if (mountValue !== undefined && mountValue !== null) {
    mountQ.setFactValue(mountValue);
  } else {
    mountQ.setFactValue(mount);
  }
  mountQ.validator = new TextValidator();
  row.addChild(mountQ);

  const fstypeQ = new OpenQuestion(`sys_vol_${rootKey}_fstype`, [
    new Translation("en", "FS Type"),
    new Translation("de", "FS Type"),
  ]);
  const fstypeValue =
    oldValues?.[`hardware.sys_vol.sys_vol_${rootKey}.sys_vol_${rootKey}_fstype`]
      ?.value;
  if (fstypeValue !== undefined && fstypeValue !== null) {
    fstypeQ.setFactValue(fstypeValue);
  } else {
    fstypeQ.setFactValue(fstype);
  }
  fstypeQ.validator = new TextValidator();
  row.addChild(fstypeQ);

  const optionQ = new OpenQuestion(`sys_vol_${rootKey}_options`, [
    new Translation("en", "Options"),
    new Translation("de", "Options"),
  ]);
  const optionValue =
    oldValues?.[
      `hardware.sys_vol.sys_vol_${rootKey}.sys_vol_${rootKey}_options`
    ]?.value;
  if (optionValue !== undefined && optionValue !== null) {
    optionQ.setFactValue(optionValue);
  } else {
    optionQ.setFactValue(options);
  }
  optionQ.validator = new TextValidator();
  row.addChild(optionQ);

  return row;
};
const generateStorage = (
  oldValue: any,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  def: {
    count: number;
  },
): {
  hardware: Row;
  config: Row;
  vg: Row;
} => {
  const key = "Storage";
  const rootKey = key.toLowerCase().replaceAll(" ", ".");
  const row = new Row(rootKey, [
    new Translation("en", key),
    new Translation("de", key),
  ]);

  const config = new Row("device_conf", [
    new Translation("en", "Storage Configuration"),
    new Translation("de", "Speicher-Konfiguration"),
  ]);
  questionsRights(config, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });

  const vg = new Row("vg", [
    new Translation("en", "Volume groups"),
    new Translation("de", "Volume groups"),
  ]);
  const vgSystem = new OpenQuestion("vg_system", [
    new Translation("en", "System"),
    new Translation("de", "System"),
  ]);
  vgSystem.validator = new TextValidator();

  const vgSystemValue = oldValue?.[`hardware.vg.vg_system`]?.value;
  if (vgSystemValue === undefined) {
    vgSystem.setFactValue("vg-system");
  } else {
    vgSystem.setFactValue(vgSystemValue);
  }
  vg.addChild(vgSystem);

  const vgData = new OpenQuestion("vg_data", [
    new Translation("en", "System"),
    new Translation("de", "System"),
  ]);
  vgData.validator = new TextValidator();

  const vgDataValue = oldValue?.[`hardware.vg.vg_data`]?.value;
  if (vgDataValue === undefined) {
    vgData.setFactValue("vg-data");
  } else {
    vgData.setFactValue(vgDataValue);
  }
  vg.addChild(vgData);
  return { hardware: row, config, vg };
};

ComplexFactory.registerClass({
  name: "NetboxDevices",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});
ComplexFactory.registerClass({
  name: "NetboxSwitches",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});
ComplexFactory.registerClass({
  name: "NetboxDacs",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});
ComplexFactory.registerClass({
  name: "NetboxProbes",
  Constructor: User as new (init?: any) => User,
  form: User.getForm,
});

export const HardwareForm = (
  oldValue: any,
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  setDefaultValues: boolean,
  defaultValues: {
    agents: {
      id: string;
      macs: string[];
      redfishMac: string;
      serialNumber: string;
    }[];
    vpc: { vpcs: Vpc[]; tenantIds: number[] };
    vpcName: string;
    nextWave: OpsanioWaves;
  },
) => {
  const hardware = new Section("hardware", [
    new Translation("en", "Hardware"),
    new Translation("de", "Hardware"),
  ]);

  const networkDevice = new ComplexQuestion(
    "servers",
    [
      new Translation("en", getTranslation("en", "anketa.appliance")),
      new Translation("de", getTranslation("de", "anketa.appliance")),
    ],
    "NetboxDevices",
  );
  questionsRights(networkDevice, {
    isInternal: true,
    isExternal: false,
  });
  hardware.addChild(networkDevice);

  const switches = new ComplexQuestion(
    "switches",
    "Switches",
    "NetboxSwitches",
  );
  questionsRights(switches, {
    isInternal: true,
    isExternal: false,
  });
  hardware.addChild(switches);

  const dac = new ComplexQuestion(
    "dac",
    [
      new Translation("en", "DAC Cables"),
      new Translation("de", "DAC-Kabel"),
    ],
    "NetboxDacs",
  );
  questionsRights(dac, {
    isInternal: true,
    isExternal: false,
  });
  hardware.addChild(dac);

  // Customer-site probes, picked from NetBox like the other hardware. The
  // questionnaire already records how many probes a site gets and what network
  // they sit on (networks.probes.*); this is which physical units were handed
  // out, so a probe can be traced to its serial and to the site it went to.
  const probes = new ComplexQuestion(
    "probes",
    [
      new Translation("en", "Probes"),
      new Translation("de", "Probes"),
    ],
    "NetboxProbes",
  );
  questionsRights(probes, {
    isInternal: true,
    isExternal: false,
  });
  hardware.addChild(probes);

  // The network switch (model / MAC / serial) is part of the supplied hardware,
  // so it lives in the Hardware section (key prefix hardware.switch.switch_row)
  // rather than the Network tab.
  hardware.addChild(generateSwitch(questionsRights));

  const storageGen = generateStorage(oldValue, questionsRights, {
    count: 3,
  });

  hardware.addChild(storageGen.hardware);
  hardware.addChild(storageGen.config);
  hardware.addChild(storageGen.vg);

  const sysVolRow = new Row("sys_vol", [
    new Translation("de", "System Volume"),
    new Translation("En", "System Volume"),
  ]);

  const changeListener = () => {
    let minTotalSize = 0;

    for (const [k, row] of sysVolRow.children) {
      for (const [key, child] of row?.children ?? []) {
        if (key.endsWith("_size")) {
          const raw: number | string = (child as any)?._fact?.raw ?? 0;
          if (typeof raw === "string") {
            const size = minTotalSize + parseFloat(raw);
            minTotalSize = size;
          } else {
            const size = minTotalSize + raw;
            minTotalSize = size;
          }
        }
      }
    }
  };

  sysVolRow.addChild(
    generateSysVol(changeListener, oldValue, "root", 30, "/", "ext4", "", 30),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "tmp",
      3,
      "/tmp",
      "ext4",
      "nodev,nosuid,noexec",
      3,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "var_tmp",
      5,
      "/var/tmp",
      "ext4",
      "nodev,nosuid,noexec",
      5,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "var",
      50,
      "/var",
      "ext4",
      "nodev,nosuid",
      50,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "var_log",
      10,
      "/var/log",
      "ext4",
      "nodev,nosuid",
      10,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "var_log_audit",
      10,
      "/var/log/audit",
      "ext4",
      "nodev,nosuid",
      10,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "home",
      15,
      "/home",
      "ext4",
      "nodev",
      15,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "opt",
      5,
      "/opt",
      "ext4",
      "nodev",
      5,
    ),
  );
  sysVolRow.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "srv",
      10,
      "/srv",
      "ext4",
      "nodev,nosuid",
      10,
    ),
  );
  const usrData = generateSysVol(
    changeListener,
    oldValue,
    "usr_local",
    5,
    "/usr/local",
    "ext4",
    "nodev",
    5,
  );
  sysVolRow.addChild(usrData);
  hardware.addChild(sysVolRow);

  const dataVolumes = new Row("data_vol", [
    new Translation("de", "Data Volume"),
    new Translation("En", "Data Volume"),
  ]);

  /*   dataVolumes.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "longhorn",
      300,
      "/var/lib/longhorn",
      "ext4",
      ""
    )
  ); */

  dataVolumes.addChild(
    generateSysVol(
      changeListener,
      oldValue,
      "data",
      -1,
      "/var/data",
      "ext4",
      "nodev,nosuid",
    ),
  );
  dataVolumes.addChild(
    generateSysVol(changeListener, oldValue, "swap", 2, "swap", "swap", "", 2),
  );

  hardware.addChild(dataVolumes);
  if (setDefaultValues) {
    networkDevice.setFactValue(
      defaultValues.agents.map((agent) => agent.id).join(","),
    );
  }
  return hardware;
};
